import { Link } from "react-router-dom";
import { Eye } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import type { Proforma } from "@/lib/proforma.api";
import { cn } from "@/lib/utils";
import { getProformaSummary, getProformaTotal } from "../proforma-calculations";
import { formatDate, formatMoney } from "../proforma-formatters";
import { MiniFact } from "./ProformaMiniFacts";
import { ProformaStatusBadge } from "./ProformaStatusBadge";

interface ProformaGridProps {
  proformas: Proforma[];
  onSelect: (proforma: Proforma) => void;
}

export function ProformaGrid({ proformas, onSelect }: ProformaGridProps) {
  if (proformas.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card px-4 py-8 text-center text-[12px] text-muted-foreground">
        Aucune proforma.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      {proformas.map((proforma) => (
        <div key={proforma.id} className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-start justify-between gap-3">
            <button type="button" className="min-w-0 text-left" onClick={() => onSelect(proforma)}>
              <p className="truncate text-[13px] font-semibold text-brand-gold-dark">
                {proforma.number}
              </p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {proforma.partySnapshot?.fullName ?? "Client non renseigné"}
              </p>
            </button>
            <ProformaStatusBadge status={proforma.status} />
          </div>

          <div className="mt-4">
            <p className="text-[22px] font-bold leading-tight tabular-nums text-foreground">
              {formatMoney(getProformaTotal(proforma))}
            </p>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {getProformaSummary(proforma)}
            </p>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2 border-t border-border pt-3">
            <MiniFact label="Créée" value={formatDate(proforma.createdAt)} />
            <MiniFact label="Expiration" value={formatDate(proforma.expiresAt)} />
          </div>

          <div className="mt-3 flex items-center justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => onSelect(proforma)}>
              <Eye size={13} /> Aperçu
            </Button>
            <Link
              to={`/proformas/${proforma.id}`}
              className={cn(buttonVariants({ variant: "outline", size: "sm" }))}
            >
              Détail
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
}
